// src/services/myAGTService.js
import axios from 'axios';

// Configuración base para las peticiones al API según el entorno
const API_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5002/api'
  : (process.env.REACT_APP_API_HEALTH_URL);

console.log("API_URL configurada:", API_URL);

// Configuración del servicio
const myAGTService = {
  // Obtener perfil público del profesional
  getPublicProfile: async (slugOrId) => {
    try {
      // Determinar si estamos obteniendo nuestro propio perfil
      const token = localStorage.getItem('agthealth_token');
      const isOwnProfile = !!token;
  
      // Usar la ruta adecuada
      const endpoint = isOwnProfile 
        ? `${API_URL}/professionals/public-profile` 
        : `${API_URL}/public/professionals/${slugOrId}`;
  
      const headers = isOwnProfile 
        ? { Authorization: `Bearer ${token}` }
        : {};
  
      const response = await axios.get(endpoint, { headers });
      console.log("Respuesta del backend (perfil):", response.data);
      return response.data;
    } catch (error) {
      console.error('Error al obtener perfil público:', error);
      throw error;
    }
  },

  // Obtener URL prefirmada para subir imagen de perfil
  getProfileImageUploadUrl: async (contentType, fileName) => {
    const token = localStorage.getItem('agthealth_token');
    
    try {
      const response = await axios.post(
        `${API_URL}/professionals/profile-image/presigned-upload`,
        { contentType, fileName },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener URL para subida de imagen de perfil:', error);
      throw error;
    }
  },

  // Guardar perfil con imagen ya subida directamente a S3
  savePublicProfileDirect: async (profileData) => {
    const token = localStorage.getItem('agthealth_token');
    try {
      // Asegurarse de que el nombre esté presente usando firstName y lastName
      if (!profileData.name || profileData.name.trim() === '') {
        const firstName = localStorage.getItem('agthealth_firstName') || '';
        const lastName = localStorage.getItem('agthealth_lastName') || '';
        profileData.name = `${firstName} ${lastName}`.trim() || 'Usuario';
      }
  
      // Log de depuración
      console.log("Guardando perfil directo con datos:", {
        ...profileData,
        profileImageUrl: profileData.profileImageUrl ? "[URL PRESENTE]" : "[SIN URL]"
      });
  
      const response = await axios.post(
        `${API_URL}/professionals/public-profile/direct`, 
        profileData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al guardar perfil público directo:', error);
      throw error;
    }
  },

  // Guardar/actualizar perfil público del profesional
  savePublicProfile: async (profileData) => {
    const token = localStorage.getItem('agthealth_token');
  
    try {
      // Si hay una imagen nueva, usar el flujo de URL prefirmada
      if (profileData.profileImage?.file) {
        const file = profileData.profileImage.file;
  
        // 1. Obtener URL prefirmada
        const uploadUrlResponse = await myAGTService.getProfileImageUploadUrl(
          file.type, 
          file.name
        );
  
        if (!uploadUrlResponse.success) {
          throw new Error('Error al obtener URL para subida de imagen');
        }
  
        const { presignedUrl, publicUrl } = uploadUrlResponse.data;
  
        // 2. Subir archivo directamente a S3
        await axios.put(presignedUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Progreso de subida de imagen de perfil: ${percentCompleted}%`);
          }
        });
  
        // 3. Eliminar el objeto de imagen del formulario y añadir la URL
        const newProfileData = { ...profileData };
        delete newProfileData.profileImage;
        newProfileData.profileImageUrl = publicUrl;
  
        // 4. Guardar el perfil con la nueva URL de imagen
        return await myAGTService.savePublicProfileDirect(newProfileData);
      } else {
        // Si no hay imagen nueva, usar el método existente con FormData
        const formData = new FormData();
  
        // CORRECCIÓN: Asegurarse de que name siempre tenga un valor
        if (!profileData.name || profileData.name.trim() === '') {
          // Usar valores almacenados en localStorage si están disponibles
          const firstName = localStorage.getItem('agthealth_firstName') || '';
          const lastName = localStorage.getItem('agthealth_lastName') || '';
          formData.append('name', `${firstName} ${lastName}`.trim() || 'Usuario');
        } else {
          formData.append('name', profileData.name);
        }
  
        // Procesar el resto de campos
        Object.keys(profileData).forEach(key => {
          if (key === 'name') {
            // Ya procesado arriba
            return;
          } else if (key === 'profileImage') {
            if (profileData.profileImage?.file) {
              formData.append('profileImage', profileData.profileImage.file);
            }
          } else if (key === 'specialties' || key === 'appointmentModes' || 
                    key === 'appointmentLocations' || key === 'professionalServices') {
            formData.append(key, JSON.stringify(profileData[key]));
          } else {
            formData.append(key, profileData[key]);
          }
        });
  
        // Log para depuración
        console.log("Datos que se enviarán al servidor:", 
          Object.fromEntries(formData.entries()));
  
        const response = await axios.post(
          `${API_URL}/professionals/public-profile`, 
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        return response.data;
      }
    } catch (error) {
      console.error('Error al guardar perfil público:', error);
      throw error;
    }
  },

  // Obtener disponibilidad del profesional
  getAvailability: async (professionalId) => {
    const token = localStorage.getItem('agthealth_token');
    try {
      const response = await axios.get(
        `${API_URL}/professionals/${professionalId}/availability`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener disponibilidad:', error);
      throw error;
    }
  },

  // Guardar disponibilidad del profesional
  saveAvailability: async (professionalId, availabilityData) => {
    const token = localStorage.getItem('agthealth_token');
    try {
      const response = await axios.post(
        `${API_URL}/professionals/${professionalId}/availability`,
        availabilityData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al guardar disponibilidad:', error);
      throw error;
    }
  },

  // Verificar disponibilidad de un slot sin bloquearlo
  checkSlotAvailability: async (profileSlug, slotData) => {
    try {
      const response = await axios.post(
        `${API_URL}/public/professionals/${profileSlug}/check-availability`,
        slotData
      );
      return response.data.available;
    } catch (error) {
      console.error('Error al verificar disponibilidad:', error);
      return false; // Si hay error, asumir que no está disponible
    }
  },

  // Reservar una cita (ruta pública)
  bookAppointment: async (profileSlug, appointmentData) => {
    try {
      const response = await axios.post(
        `${API_URL}/public/professionals/${profileSlug}/book`,
        appointmentData
      );
      return response.data;
    } catch (error) {
      console.error('Error al reservar cita:', error);
      throw error;
    }
  },

  // Bloquear temporalmente una hora
  blockTimeSlot: async (profileSlug, timeSlot) => {
    try {
      const response = await axios.post(
        `${API_URL}/public/professionals/${profileSlug}/block-slot`,
        timeSlot // Enviar directamente el objeto, sin envolverlo
      );
      return response.data;
    } catch (error) {
      console.error('Error al bloquear horario:', error);
      throw error;
    }
  },

  // Liberar un bloqueo temporal
  releaseTimeSlot: async (profileSlug, lockId) => {
    try {
      const response = await axios.post(
        `${API_URL}/public/professionals/${profileSlug}/release-slot`,
        { lockId }
      );
      return response.data;
    } catch (error) {
      console.error('Error al liberar horario:', error);
      throw error;
    }
  },
  
  // Verificar estado de un mensaje de WhatsApp
  checkWhatsAppMessageStatus: async (messageId) => {
    try {
      const response = await axios.get(
        `${API_URL}/webhook/message-status/${messageId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error al verificar estado de mensaje WhatsApp:', error);
      return { status: 'unknown' };
    }
  },
  
  // Reenviar confirmación de cita
  resendAppointmentConfirmation: async (appointmentId, phoneNumber) => {
    try {
      const response = await axios.post(
        `${API_URL}/public/resend-confirmation/${appointmentId}`,
        { phoneNumber }
      );
      return response.data;
    } catch (error) {
      console.error('Error al reenviar confirmación:', error);
      throw error;
    }
  },

  // Obtener mis historias
  getStories: async (professionalId) => {
    const token = localStorage.getItem('agthealth_token');
    try {
      const response = await axios.get(
        `${API_URL}/stories/my-stories`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener mis historias:', error);
      throw error;
    }
  },

  // Obtener historias de un profesional
  getPublicStories: async (slug) => {
    try {
      const response = await axios.get(`${API_URL}/public/professionals/${slug}/stories`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener historias públicas:', error);
      throw error;
    }
  },
  
  // Obtener URL prefirmada para subir historia
  getStoryUploadUrl: async (contentType, fileName) => {
    const token = localStorage.getItem('agthealth_token');
    
    try {
      const response = await axios.post(
        `${API_URL}/stories/presigned-upload`,
        { contentType, fileName },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al obtener URL de subida:', error);
      throw error;
    }
  },
  
  // Registrar historia después de subida directa
  registerStory: async (imageUrl, caption) => {
    const token = localStorage.getItem('agthealth_token');
    
    try {
      const response = await axios.post(
        `${API_URL}/stories/register`,
        { imageUrl, caption },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al registrar historia:', error);
      throw error;
    }
  },
    
  // Subir una historia (usando URLs prefirmadas)
  uploadStory: async (imageFile, caption) => {
    try {
      // 1. Obtener URL prefirmada
      const uploadUrlResponse = await myAGTService.getStoryUploadUrl(
        imageFile.type, 
        imageFile.name
      );
      
      if (!uploadUrlResponse.success) {
        throw new Error('Error al obtener URL para subida');
      }
      
      const { presignedUrl, publicUrl } = uploadUrlResponse.data;
      
      // 2. Subir archivo directamente a S3
      await axios.put(presignedUrl, imageFile, {
        headers: {
          'Content-Type': imageFile.type,
        },
        onUploadProgress: (progressEvent) => {
          // Aquí podrías manejar una barra de progreso si lo deseas
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Progreso de subida: ${percentCompleted}%`);
        }
      });
      
      // 3. Registrar la historia en la base de datos
      const registerResponse = await myAGTService.registerStory(publicUrl, caption);
      
      return registerResponse;
    } catch (error) {
      console.error('Error en el proceso de subida de historia:', error);
      throw error;
    }
  },
  
  // Eliminar una historia
  deleteStory: async (storyId) => {
    const token = localStorage.getItem('agthealth_token');
  
    try {
      const response = await axios.delete(
        `${API_URL}/stories/${storyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al eliminar historia:', error);
      throw error;
    }
  },
  
  // Ver una historia (incrementar vistas)
  viewStory: async (storyId) => {
    try {
      const response = await axios.post(`${API_URL}/stories/${storyId}/view`);
      return response.data;
    } catch (error) {
      console.error('Error al ver historia:', error);
      // No relanzar el error para no interrumpir la visualización
      return { success: false };
    }
  },
  
  // Generar un enlace de reunión de Zoom
  generateZoomMeeting: async (meetingData) => {
    const token = localStorage.getItem('agthealth_token');
    
    try {
      const response = await axios.post(
        `${API_URL}/meetings/zoom`,
        meetingData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al generar reunión de Zoom:', error);
      throw error;
    }
  },
  
  // Generar un enlace de reunión de Google Meet
  generateGoogleMeetLink: async (meetingData) => {
    const token = localStorage.getItem('agthealth_token');
    
    try {
      const response = await axios.post(
        `${API_URL}/meetings/google-meet`,
        meetingData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error al generar reunión de Google Meet:', error);
      throw error;
    }
  },

  sendQuery: async (query, conversationHistory = []) => {
    const token = localStorage.getItem('agthealth_token');
  
    try {
      const response = await axios.post(
        `${API_URL}/bedrock/query`,
        { 
          query,
          conversationHistory 
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return {
        success: true,
        message: response.data.response,
        conversationHistory: response.data.conversationHistory
      };
    } catch (error) {
      console.error('Error al enviar consulta al asistente:', error);
      return {
        success: false,
        message: "Lo siento, ha ocurrido un error al procesar tu consulta. Por favor, inténtalo de nuevo más tarde."
      };
    }
  },
};

export default myAGTService;